/**
 * O LANÇAMENTO EM CINCO PERGUNTAS — a lógica, sem desenho.
 *
 * É a entrega "Lançamento de abastecimento" que o dono mandou em 25/10/2026:
 * o tablet do comboio, o celular do comboísta e a linha rápida da mesa fazem a
 * MESMA coisa, com a mesma ordem e as mesmas verificações. Só o desenho muda.
 *
 * ## O que muda em relação ao formulário de hoje
 *
 * O formulário da mesa pergunta onze coisas. Aqui a pessoa responde **quatro**
 * — máquina, produto, litros e medidor — e o resto o sistema resolve:
 *
 *   - **fixos no aparelho:** a origem (o comboio daquele tablet) e quem lança;
 *   - **automáticos:** data e hora (agora), obra e centro de custo (da
 *     alocação vigente da máquina).
 *
 * Isso não é simplificação de tela: é a diferença entre alguém de pé, na
 * frente de serviço, com luva e sol na tela, conseguir ou não lançar. Cada
 * campo a mais é uma chance a mais de o abastecimento não ser registrado — e
 * abastecimento não registrado é o diesel que some do consumo da máquina.
 *
 * ## As quatro verificações, e o que cada uma faz quando falha
 *
 * Só UMA barra o lançamento, e é a do medidor: medidor menor que a última
 * leitura é número errado, e gravá-lo estraga o consumo do intervalo inteiro.
 * As outras três avisam:
 *
 *   - **cabe no tanque da máquina** — acima da capacidade é erro, porque o
 *     volume não cabe fisicamente;
 *   - **saldo na origem** — acima do saldo é erro, e o servidor recusa de
 *     qualquer forma (o tanque ficaria negativo);
 *   - **consumo na faixa** — fora dela **lança** e vai para conferência. É a
 *     regra que o módulo inteiro segue desde a 0011: sistema que recusa o que
 *     aconteceu produz operação que para de lançar.
 *
 * ## Por que a lógica mora aqui, e não dentro das telas
 *
 * São três telas respondendo a mesma pergunta. Com a regra dentro de cada uma,
 * o celular recusaria um volume que a mesa aceita, e ninguém saberia qual das
 * duas está certa até alguém comparar as duas a mão.
 */
import { textoDoTeclado } from '@/mobile/teclado';

import { textoDaFaixa } from './consumo';
import type { EquipamentoDoModulo, FaixaConsumo } from './tipos';
import type { NivelDaVerificacao, Verificacao } from './verificacao';

/* ==========================================================================
 * As cinco etapas
 * ======================================================================= */

export const ETAPAS = ['maquina', 'produto', 'litros', 'medidor', 'confirmacao'] as const;

export type EtapaDoLancamento = (typeof ETAPAS)[number];

/** O rótulo curto de cada etapa — o que aparece no resumo lateral do tablet. */
export const ROTULO_DA_ETAPA: Readonly<Record<EtapaDoLancamento, string>> = {
  maquina: 'Máquina',
  produto: 'Produto',
  litros: 'Litros',
  medidor: 'Medidor',
  confirmacao: 'Confirmação',
};

/**
 * O que a pessoa vê. São PERGUNTAS, e não rótulos de campo: quem está de pé
 * na frente de serviço lê uma pergunta e responde; rótulo pede que ela
 * descubra sozinha o que fazer com aquele campo.
 */
export const PERGUNTA_DA_ETAPA: Readonly<Record<EtapaDoLancamento, string>> = {
  maquina: 'Qual máquina?',
  produto: 'Qual produto?',
  litros: 'Quantos litros?',
  medidor: 'Qual o medidor?',
  confirmacao: 'Confira e lance',
};

/** O rascunho, como ele vive na tela: os números ficam como foram digitados. */
export interface RascunhoDoLancamento {
  readonly equipamentoId: string | null;
  readonly produtoId: string | null;
  /** Como digitado no teclado próprio: `"320"`, `"4832,5"`. Vazio é "ainda não". */
  readonly litros: string;
  readonly medidor: string;
  /**
   * DE ONDE SAIU O DIESEL, e é a pergunta que estava faltando (26/10/2026).
   *
   * O dono: *"em lançamento eu não consigo escolher tipo"*. A linha rápida
   * nascia `interno` e não oferecia a escolha: quem abastecia no posto tinha
   * de trocar para o formulário inteiro, e ele já havia recusado esse caminho
   * — *"eu não quero simplesmente clicar em mais campos e ele ir para
   * completa"*. Abastecer no posto não é exceção; é o outro lado do mesmo
   * lançamento, e o banco (0008) trata os dois no mesmo `insert`.
   */
  readonly tipo: 'interno' | 'externo';
  /** Só no EXTERNO: o posto onde se abasteceu. No interno a origem é o tanque. */
  readonly postoId: string | null;
  /** Só no externo, como digitado: `"6,41"`. O interno herda o preço do tanque. */
  readonly preco: string;
  /**
   * Só no externo, e OBRIGATÓRIO: o número do cupom ou da nota. O banco recusa
   * o externo sem ele desde a 0008 — "externo sem comprovante é pagamento sem
   * lastro" —, então a linha o cobra antes de enviar, e não depois do 422.
   */
  readonly comprovante: string;
}

export const RASCUNHO_VAZIO: RascunhoDoLancamento = Object.freeze({
  equipamentoId: null,
  produtoId: null,
  litros: '',
  medidor: '',
  tipo: 'interno',
  postoId: null,
  preco: '',
  comprovante: '',
});

/** O produto escolhido, no mínimo que as verificações precisam dele. */
export interface ProdutoDoLancamento {
  readonly id: string;
  readonly nome: string;
  /** `combustivel`, `arla`, `lubrificante`, `outro`. */
  readonly categoria: string;
}

/**
 * O que a tela sabe e o rascunho não: a máquina escolhida já resolvida, o
 * produto, o saldo da origem e as duas leituras que sustentam as sugestões.
 */
export interface ContextoDoLancamento {
  readonly equipamento: EquipamentoDoModulo | null;
  readonly produto: ProdutoDoLancamento | null;
  /** Litros daquele produto na origem. `null` quando a origem não é tanque próprio. */
  readonly saldoDaOrigem: number | null;
  /** "Comboio 02", "Tanque do pátio" — o que a frase de erro precisa nomear. */
  readonly nomeDaOrigem: string;
  /** A leitura do medidor no abastecimento ANTERIOR desta máquina e deste produto. */
  readonly medidorDoUltimoAbastecimento: number | null;
  /** A leitura mais recente de qualquer origem (apontamento, oficina), para a sugestão. */
  readonly medidorSugerido: number | null;
  /** Quando a última leitura foi feita, para a frase "última leitura: X, ontem 17:04". */
  readonly medidorLidoEm: Date | null;
}

/* ==========================================================================
 * As verificações
 * ======================================================================= */

const UM = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const ZERO = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 1 });

/** A unidade do medidor desta máquina: hora ou quilômetro. */
export function unidadeDoMedidor(equipamento: EquipamentoDoModulo | null): 'h' | 'km' {
  return equipamento?.tipoMedidor === 'odometro' ? 'km' : 'h';
}

function faixaDaMaquina(equipamento: EquipamentoDoModulo | null): FaixaConsumo | null {
  return equipamento?.faixa ?? null;
}

/**
 * As quatro verificações do protótipo, na ordem em que ele as desenha.
 *
 * Cada uma responde mesmo sem a resposta da pessoa: enquanto falta o número,
 * o nível é `pendente` e o detalhe diz contra o que ele vai ser conferido. É
 * isso que faz as fichas aparecerem já na etapa dos litros, e não só no fim.
 */
export function verificacoesDoRascunho(
  rascunho: RascunhoDoLancamento,
  contexto: ContextoDoLancamento,
): readonly Verificacao[] {
  const { equipamento, produto, saldoDaOrigem, nomeDaOrigem, medidorDoUltimoAbastecimento } = contexto;
  const medida = unidadeDoMedidor(equipamento);
  const litros = numeroDigitado(rascunho.litros);
  const medidor = numeroDigitado(rascunho.medidor);
  const lista: Verificacao[] = [];

  if (equipamento === null) {
    return [
      {
        nivel: 'pendente',
        titulo: 'Escolha a máquina',
        detalhe: 'As verificações aparecem depois: elas são contra o tanque, a leitura e a faixa desta máquina.',
      },
    ];
  }

  /* ------------------------------------------------------------- 1. medidor */
  const ultima = equipamento.ultimaLeitura;
  if (medidor === null) {
    lista.push({
      nivel: 'pendente',
      titulo: 'Medidor',
      detalhe:
        ultima === null
          ? `Esta máquina ainda não tem leitura registrada. A primeira entra com este lançamento.`
          : `Última leitura: ${ZERO(ultima)} ${medida}.`,
    });
  } else if (ultima !== null && medidor < ultima) {
    lista.push({
      nivel: 'erro',
      titulo: 'Medidor menor que a última leitura',
      detalhe: `${ZERO(medidor)} ${medida} é menor que ${ZERO(ultima)} ${medida}. Confira o número no painel da máquina.`,
    });
  } else {
    lista.push({
      nivel: 'ok',
      titulo: 'Medidor em ordem',
      detalhe:
        ultima === null
          ? `${ZERO(medidor)} ${medida}, a primeira leitura desta máquina.`
          : `${ZERO(medidor)} ${medida}, maior que a leitura anterior.`,
    });
  }

  /* -------------------------------------------------- 2. o tanque da máquina */
  const capacidade = equipamento.capacidadeTanqueL ?? null;
  if (capacidade === null) {
    /* Sem capacidade cadastrada o sistema não tem contra o que comparar, e
       dizer "cabe" sem saber é a mentira mais fácil de acreditar aqui. */
    lista.push({
      nivel: 'naoSeAplica',
      titulo: 'Tanque da máquina',
      detalhe: 'Sem a capacidade do tanque no cadastro do equipamento, o sistema não confere o volume.',
    });
  } else if (litros === null) {
    lista.push({ nivel: 'pendente', titulo: 'Tanque da máquina', detalhe: `Cabem até ${ZERO(capacidade)} L.` });
  } else if (litros > capacidade) {
    lista.push({
      nivel: 'erro',
      titulo: 'Acima do tanque da máquina',
      detalhe: `${ZERO(litros)} L não cabem no tanque de ${ZERO(capacidade)} L da ${equipamento.codigo}.`,
    });
  } else {
    lista.push({
      nivel: 'ok',
      titulo: 'Cabe no tanque',
      detalhe: `${ZERO(litros)} L de ${ZERO(capacidade)} L da ${equipamento.codigo}.`,
    });
  }

  /* ------------------------------- 2b. o cupom, e só no abastecimento no posto */
  if (rascunho.tipo === 'externo') {
    const temPosto = rascunho.postoId !== null && rascunho.postoId !== '';
    const temCupom = rascunho.comprovante.trim() !== '';
    if (!temPosto) {
      lista.push({
        nivel: 'pendente',
        titulo: 'Posto',
        detalhe: 'Escolha o posto: é por ele que a cobrança é conferida depois.',
      });
    } else if (!temCupom) {
      /* ERRO, e não aviso: o banco recusa o externo sem comprovante (0008). A
         linha avisa antes para a pessoa não perder o que digitou num 422. */
      lista.push({
        nivel: 'erro',
        titulo: 'Cupom do posto',
        detalhe: 'Informe o número do cupom ou da nota. Sem ele o lançamento não entra, e a cobrança não tem contra o que ser conferida.',
      });
    } else {
      lista.push({
        nivel: 'ok',
        titulo: 'Cupom do posto',
        detalhe: `Cupom ${rascunho.comprovante.trim()}, para conferir a cobrança depois.`,
      });
    }
  }

  /* ------------------------------------------------------- 3. saldo na origem */
  if (saldoDaOrigem === null) {
    lista.push({
      nivel: 'naoSeAplica',
      titulo: 'Saldo na origem',
      detalhe: 'O abastecimento no posto não tira volume de tanque nenhum.',
    });
  } else if (litros === null) {
    lista.push({ nivel: 'pendente', titulo: `Saldo em ${nomeDaOrigem}`, detalhe: `${ZERO(saldoDaOrigem)} L agora.` });
  } else if (litros > saldoDaOrigem) {
    lista.push({
      nivel: 'erro',
      titulo: `Saldo insuficiente em ${nomeDaOrigem}`,
      detalhe: `Lá há ${ZERO(saldoDaOrigem)} L${produto ? ` de ${produto.nome}` : ''}, e o lançamento pede ${ZERO(litros)} L.`,
    });
  } else {
    lista.push({
      nivel: 'ok',
      titulo: `Saldo em ${nomeDaOrigem}`,
      detalhe: `Sobram ${ZERO(saldoDaOrigem - litros)} L depois deste lançamento.`,
    });
  }

  /* --------------------------------------------------- 4. o consumo e a faixa */
  const faixa = faixaDaMaquina(equipamento);
  const unidadeDoConsumo = medida === 'km' ? 'L/km' : 'L/h';
  const escrita = faixa === null ? null : textoDaFaixa(faixa.minimo, faixa.maximo, unidadeDoConsumo);
  if (produto !== null && produto.categoria !== 'combustivel') {
    lista.push({
      nivel: 'naoSeAplica',
      titulo: `Consumo por ${medida === 'km' ? 'quilômetro' : 'hora'}`,
      detalhe: `Não se aplica a ${produto.nome}: a faixa é de litro de combustível.`,
    });
  } else {
    const consumo = consumoDoRascunho(rascunho, contexto);
    if (consumo === null) {
      lista.push({
        nivel: 'pendente',
        titulo: `Consumo por ${medida === 'km' ? 'quilômetro' : 'hora'}`,
        detalhe:
          medidorDoUltimoAbastecimento === null
            ? 'Sai no próximo abastecimento desta máquina, que fecha o primeiro intervalo.'
            : 'Calculado com os litros e o medidor.',
      });
    } else if (escrita === null) {
      lista.push({
        nivel: 'naoSeAplica',
        titulo: `Consumo de ${UM(consumo)} ${unidadeDoConsumo}`,
        detalhe: 'Sem faixa cadastrada para esta máquina, o sistema não tem contra o que comparar.',
      });
    } else if (foraDaFaixa(consumo, faixa)) {
      lista.push({
        nivel: 'atencao',
        titulo: `Consumo ${consumo > (faixa?.maximo ?? consumo) ? 'acima' : 'abaixo'} da faixa`,
        detalhe: `${UM(consumo)} ${unidadeDoConsumo}, fora da faixa de ${escrita}. Lança e vai para conferência.`,
      });
    } else {
      lista.push({
        nivel: 'ok',
        titulo: 'Consumo dentro da faixa',
        detalhe: `${UM(consumo)} ${unidadeDoConsumo}. A faixa da máquina é de ${escrita}.`,
      });
    }
  }

  return lista;
}

/** `true` quando o consumo caiu fora da faixa cadastrada; `false` dentro dela. */
function foraDaFaixa(consumo: number, faixa: FaixaConsumo | null): boolean {
  if (faixa === null) return false;
  if (faixa.minimo !== null && consumo < faixa.minimo) return true;
  if (faixa.maximo !== null && consumo > faixa.maximo) return true;
  return false;
}

/**
 * O consumo que ESTE lançamento implica, antes de ele existir.
 *
 * Litros deste abastecimento sobre o que a máquina andou desde o anterior —
 * a mesma conta que o banco refaz depois. `null` quando falta uma das pontas
 * ou quando o medidor não andou: dividir por zero daria um número com cara de
 * consumo, e é justamente o caso em que não se sabe nada.
 */
export function consumoDoRascunho(
  rascunho: RascunhoDoLancamento,
  contexto: ContextoDoLancamento,
): number | null {
  const litros = numeroDigitado(rascunho.litros);
  const medidor = numeroDigitado(rascunho.medidor);
  const anterior = contexto.medidorDoUltimoAbastecimento;
  if (litros === null || medidor === null || anterior === null) return null;
  const andou = medidor - anterior;
  if (andou <= 0) return null;
  return litros / andou;
}

/* ==========================================================================
 * O que cada etapa precisa, e quando ela deixa avançar
 * ======================================================================= */

function numeroDigitado(texto: string): number | null {
  if (texto === '' || texto === ',') return null;
  const n = Number(texto.replace(',', '.'));
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** O nível mais grave da lista — `erro` manda, depois `atencao`. */
function pior(verificacoes: readonly Verificacao[]): NivelDaVerificacao | null {
  if (verificacoes.some((v) => v.nivel === 'erro')) return 'erro';
  if (verificacoes.some((v) => v.nivel === 'atencao')) return 'atencao';
  return null;
}

/**
 * A etapa está respondida? Não confunde "respondida" com "certa": o volume
 * acima do tanque está respondido e errado, e é por isso que `podeAvancar`
 * pergunta as duas coisas.
 */
export function etapaRespondida(etapa: EtapaDoLancamento, rascunho: RascunhoDoLancamento): boolean {
  if (etapa === 'maquina') return rascunho.equipamentoId !== null;
  if (etapa === 'produto') return rascunho.produtoId !== null;
  if (etapa === 'litros') return numeroDigitado(rascunho.litros) !== null;
  if (etapa === 'medidor') return numeroDigitado(rascunho.medidor) !== null;
  return false;
}

/**
 * O botão "Próximo" liga quando a etapa está respondida E o que ela respondeu
 * não é erro. Deixar avançar com erro empurra a recusa para a última tela,
 * depois de a pessoa ter digitado tudo — que é onde ela menos quer descobrir.
 */
export function podeAvancar(
  etapa: EtapaDoLancamento,
  rascunho: RascunhoDoLancamento,
  contexto: ContextoDoLancamento,
): boolean {
  if (etapa === 'confirmacao') return podeLancar(rascunho, contexto);
  if (!etapaRespondida(etapa, rascunho)) return false;
  if (etapa === 'litros' || etapa === 'medidor') {
    const erros = verificacoesDoRascunho(rascunho, contexto).filter((v) => v.nivel === 'erro');
    const sobreEsta = etapa === 'litros' ? /tanque|Saldo/i : /[Mm]edidor/;
    return !erros.some((e) => sobreEsta.test(e.titulo));
  }
  return true;
}

/** Tudo respondido e nenhum erro: é o que o botão de lançar pergunta. */
export function podeLancar(rascunho: RascunhoDoLancamento, contexto: ContextoDoLancamento): boolean {
  const respondidas = ETAPAS.filter((e) => e !== 'confirmacao').every((e) => etapaRespondida(e, rascunho));
  /* O EXTERNO PEDE DUAS RESPOSTAS A MAIS, e elas não são etapa do fluxo de
     cinco perguntas: o posto, porque sem ele não há a quem conferir a
     cobrança, e o cupom, porque o banco recusa sem ele. A falta do cupom já
     vira `erro` nas verificações; a do posto fica só aqui, para a linha não
     mostrar erro vermelho antes de a pessoa ter tido a chance de escolher. */
  const doPosto =
    rascunho.tipo !== 'externo' || (rascunho.postoId !== null && rascunho.postoId !== '' && rascunho.comprovante.trim() !== '');
  return respondidas && doPosto && pior(verificacoesDoRascunho(rascunho, contexto)) !== 'erro';
}

/** O rótulo do botão principal: ele REPETE o volume no fim, para conferir antes de gravar. */
export function rotuloDoBotao(etapa: EtapaDoLancamento, rascunho: RascunhoDoLancamento): string {
  if (etapa !== 'confirmacao') return 'Próximo';
  const litros = numeroDigitado(rascunho.litros);
  return litros === null ? 'Lançar' : `Lançar ${textoDoTeclado(rascunho.litros)} L`;
}

/* ==========================================================================
 * O resumo, que é também a navegação
 * ======================================================================= */

export type EstadoDaEtapa = 'feita' | 'atual' | 'erro' | 'aberta';

export interface LinhaDoResumo {
  readonly etapa: EtapaDoLancamento;
  readonly rotulo: string;
  /** O que foi respondido, já escrito — ou o convite, quando ainda não foi. */
  readonly valor: string;
  readonly estado: EstadoDaEtapa;
}

/**
 * O resumo lateral do tablet e a barra de etapas do celular saem daqui, e
 * tocar numa linha volta para aquela etapa. É a mesma lista nos dois: a ordem
 * das perguntas não pode mudar de aparelho para aparelho.
 */
function semVirgulaSolta(texto: string): string {
  return textoDoTeclado(texto.endsWith(',') ? texto.slice(0, -1) : texto);
}

export function resumoDoLancamento(
  rascunho: RascunhoDoLancamento,
  contexto: ContextoDoLancamento,
  etapaAtual: EtapaDoLancamento,
): readonly LinhaDoResumo[] {
  const verificacoes = verificacoesDoRascunho(rascunho, contexto);
  const temErro = (padrao: RegExp) => verificacoes.some((v) => v.nivel === 'erro' && padrao.test(v.titulo));
  const medida = unidadeDoMedidor(contexto.equipamento);

  const valores: Readonly<Record<EtapaDoLancamento, string>> = {
    maquina: contexto.equipamento
      ? `${contexto.equipamento.codigo} · ${contexto.equipamento.tipo ?? 'sem tipo'}`
      : 'Escolher',
    produto: contexto.produto?.nome ?? 'Escolher',
    /* A virgula pendurada no fim some do RESUMO: quem digitou "85," e ainda
       vai digitar a casa decimal, e "85, km" num resumo parece defeito. No
       campo em que se digita ela fica, porque ali ela e o cursor. */
    litros: rascunho.litros === '' ? 'Informar' : `${semVirgulaSolta(rascunho.litros)} L`,
    medidor: rascunho.medidor === '' ? 'Informar' : `${semVirgulaSolta(rascunho.medidor)} ${medida}`,
    confirmacao: 'Revisar e lançar',
  };
  const erros: Readonly<Record<EtapaDoLancamento, boolean>> = {
    maquina: false,
    produto: false,
    litros: temErro(/tanque|Saldo/i),
    medidor: temErro(/[Mm]edidor/),
    confirmacao: false,
  };

  return ETAPAS.map((etapa) => {
    const respondida = etapaRespondida(etapa, rascunho);
    const estado: EstadoDaEtapa = erros[etapa]
      ? 'erro'
      : etapa === etapaAtual
        ? 'atual'
        : respondida
          ? 'feita'
          : 'aberta';
    return { etapa, rotulo: ROTULO_DA_ETAPA[etapa], valor: valores[etapa], estado };
  });
}

/**
 * UM TANQUE POR PRODUTO — a origem de cada produto, quando há mais de uma.
 *
 * A empresa tem o tanque do pátio e o comboio, e os dois guardam Diesel S10.
 * Listando os tanques, a escolha do produto mostrava "ÓLEO DIESEL S10" duas
 * vezes, sem nada que distinguisse uma da outra: duas opções idênticas numa
 * tela de quatro perguntas é a pergunta que ninguém sabe responder.
 *
 * A regra é a mesma que o formulário completo já usava: o tanque **padrão**
 * manda; sem padrão, o de maior saldo — que é de onde a operação tiraria de
 * qualquer forma. Quem precisa escolher a origem continua tendo o formulário
 * completo, que pergunta o tanque.
 */
export function origemPorProduto<T extends { readonly produtoId: string; readonly padrao: boolean; readonly saldoCalculadoL: number }>(
  tanques: readonly T[],
): readonly T[] {
  const porProduto = new Map<string, T>();
  for (const tanque of tanques) {
    const atual = porProduto.get(tanque.produtoId);
    if (atual === undefined) {
      porProduto.set(tanque.produtoId, tanque);
      continue;
    }
    if (atual.padrao && !tanque.padrao) continue;
    if (!atual.padrao && tanque.padrao) {
      porProduto.set(tanque.produtoId, tanque);
      continue;
    }
    if (tanque.saldoCalculadoL > atual.saldoCalculadoL) porProduto.set(tanque.produtoId, tanque);
  }
  return [...porProduto.values()];
}

/**
 * A ORIGEM ESCOLHIDA NO APARELHO (pedido do dono, 27/10/2026): o lançamento
 * guiado abria sempre no tanque padrão, sem como trocar — e o comboísta que
 * tira do comboio, e não do pátio, lançava da origem errada.
 *
 * O tanque escolhido passa a ser a origem do produto DELE; os outros produtos
 * seguem a regra de `origemPorProduto`. Escolha que não existe mais (tanque
 * desativado, id de outro cadastro) é ignorada, e a origem volta ao padrão.
 */
export function origemComEscolha<T extends { readonly id: string; readonly produtoId: string; readonly padrao: boolean; readonly saldoCalculadoL: number }>(
  todos: readonly T[],
  escolhidoId: string | null,
): readonly T[] {
  const padrao = todos.filter((t) => t.padrao);
  const base = origemPorProduto(padrao.length > 0 ? padrao : todos);
  const escolhido = escolhidoId === null ? undefined : todos.find((t) => t.id === escolhidoId);
  if (escolhido === undefined) return base;
  return [escolhido, ...base.filter((t) => t.produtoId !== escolhido.produtoId)];
}

/* ==========================================================================
 * As sugestões de um toque
 * ======================================================================= */

export interface Sugestao {
  readonly rotulo: string;
  /** O que ela escreve no campo, já no formato do teclado (vírgula decimal). */
  readonly valor: string;
  /** A primeira, que é a calculada — as outras são volumes redondos. */
  readonly principal: boolean;
}

const VOLUMES_REDONDOS = [100, 200, 300];

/**
 * "Completar ≈ 340 L" — quanto a máquina provavelmente queimou desde o último
 * abastecimento, pela faixa dela, arredondado para a dezena e limitado ao
 * tanque.
 *
 * É ESTIMATIVA, e o "≈" está no rótulo por isso. Ela existe para poupar a
 * digitação do caso comum (completar o tanque), não para substituir a leitura
 * da bomba — o número continua editável, e é o digitado que vale.
 *
 * Sem faixa, sem capacidade ou sem as duas leituras, ela simplesmente não
 * aparece: sugestão calculada sobre buraco é chute com cara de conta.
 */
export function sugestaoDeCompletar(contexto: ContextoDoLancamento): number | null {
  const { equipamento, medidorDoUltimoAbastecimento, medidorSugerido } = contexto;
  const faixa = faixaDaMaquina(equipamento);
  const capacidade = equipamento?.capacidadeTanqueL ?? null;
  if (faixa === null || capacidade === null) return null;
  if (medidorDoUltimoAbastecimento === null || medidorSugerido === null) return null;
  const andou = medidorSugerido - medidorDoUltimoAbastecimento;
  if (andou <= 0) return null;
  const meio =
    faixa.minimo !== null && faixa.maximo !== null
      ? (faixa.minimo + faixa.maximo) / 2
      : (faixa.minimo ?? faixa.maximo ?? 0);
  if (meio <= 0) return null;
  const estimado = Math.round((meio * andou) / 10) * 10;
  if (estimado <= 0) return null;
  return Math.min(capacidade, estimado);
}

/**
 * As sugestões da etapa dos litros: a calculada primeiro, depois os volumes
 * redondos que cabem no tanque e no saldo. Oferecer "300 L" para um tanque de
 * 250 L é oferecer um erro em um toque.
 */
export function sugestoesDeLitros(contexto: ContextoDoLancamento): readonly Sugestao[] {
  const capacidade = contexto.equipamento?.capacidadeTanqueL ?? null;
  const teto = Math.min(capacidade ?? Infinity, contexto.saldoDaOrigem ?? Infinity);
  const completar = sugestaoDeCompletar(contexto);
  const lista: Sugestao[] = [];
  if (completar !== null) {
    lista.push({ rotulo: `Completar ≈ ${ZERO(completar)} L`, valor: String(completar), principal: true });
  }
  for (const volume of VOLUMES_REDONDOS) {
    if (volume <= teto) lista.push({ rotulo: `${ZERO(volume)} L`, valor: String(volume), principal: false });
  }
  return lista;
}

/**
 * A sugestão do medidor: a leitura mais recente que o sistema tem da máquina —
 * a do apontamento do turno, quando ela existe.
 *
 * Só aparece quando é MAIOR que a leitura do último abastecimento: oferecer um
 * número que a própria verificação vai recusar é oferecer um erro em um toque.
 */
export function sugestaoDeMedidor(contexto: ContextoDoLancamento): Sugestao | null {
  const { medidorSugerido, equipamento } = contexto;
  if (medidorSugerido === null) return null;
  const piso = equipamento?.ultimaLeitura ?? null;
  if (piso !== null && medidorSugerido < piso) return null;
  const medida = unidadeDoMedidor(equipamento);
  return {
    rotulo: `Usar ${ZERO(medidorSugerido)} ${medida}`,
    valor: String(medidorSugerido).replace('.', ','),
    principal: true,
  };
}

/* ==========================================================================
 * O que a tela diz em cada etapa
 * ======================================================================= */

/**
 * A frase abaixo da pergunta: ela diz ONDE LER o número, e contra o que ele
 * vai ser conferido. "Leia na bomba do comboio" é instrução; "Litros" é
 * rótulo. A diferença aparece na primeira vez que alguém usa a tela sem
 * ninguém do lado.
 */
export function explicacaoDaEtapa(etapa: EtapaDoLancamento, contexto: ContextoDoLancamento): string {
  const maquina = contexto.equipamento;
  const medida = unidadeDoMedidor(maquina);
  const capacidade = maquina?.capacidadeTanqueL ?? null;
  if (etapa === 'maquina') return 'Toque na máquina que está abastecendo.';
  if (etapa === 'produto') {
    return maquina === null ? 'Escolha o produto.' : `O que sai agora para a ${maquina.codigo}.`;
  }
  if (etapa === 'litros') {
    if (maquina === null) return 'Leia na bomba.';
    const daOrigem = contexto.saldoDaOrigem === null ? '' : ` em ${contexto.nomeDaOrigem}`;
    return capacidade === null
      ? `Leia na bomba${daOrigem}.`
      : `Leia na bomba${daOrigem}. O tanque da ${maquina.codigo} leva até ${ZERO(capacidade)} L.`;
  }
  if (etapa === 'medidor') {
    return maquina === null
      ? 'Leia no painel da máquina.'
      : `Leia no painel da ${maquina.codigo}, em ${medida === 'km' ? 'quilômetros' : 'horas'}.`;
  }
  return 'Tudo certo? Toque em um item para alterar.';
}

/** A dica abaixo do número grande, que repete contra o que ele é conferido. */
export function dicaDaEntrada(etapa: EtapaDoLancamento, contexto: ContextoDoLancamento): string {
  const maquina = contexto.equipamento;
  if (etapa === 'litros') {
    const capacidade = maquina?.capacidadeTanqueL ?? null;
    const partes = [
      capacidade === null ? null : `Tanque da máquina: ${ZERO(capacidade)} L`,
      contexto.saldoDaOrigem === null ? null : `em ${contexto.nomeDaOrigem}: ${ZERO(contexto.saldoDaOrigem)} L`,
    ].filter((p): p is string => p !== null);
    return partes.join(' · ');
  }
  if (etapa === 'medidor') {
    const ultima = maquina?.ultimaLeitura ?? null;
    if (ultima === null) return 'Esta máquina ainda não tem leitura registrada.';
    const quando = contexto.medidorLidoEm;
    return `Última leitura: ${ZERO(ultima)} ${unidadeDoMedidor(maquina)}${quando ? `, ${quandoPorExtenso(quando)}` : ''}.`;
  }
  return '';
}

/** "hoje, 09:12" · "ontem, 17:04" · "14/10, 07:21" — como o protótipo escreve. */
export function quandoPorExtenso(quando: Date, agora: Date = new Date()): string {
  const hora = quando.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const dia = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diasAtras = Math.round((dia(agora) - dia(quando)) / 86_400_000);
  if (diasAtras === 0) return `hoje, ${hora}`;
  if (diasAtras === 1) return `ontem, ${hora}`;
  return `${quando.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}, ${hora}`;
}

/**
 * O que a tela de concluído diz. O protótipo é explícito: o retorno repete o
 * volume, a máquina, o medidor, o consumo e **o saldo que sobrou** — porque
 * quem está no comboio precisa saber quanto ainda tem antes da próxima
 * máquina, e descobrir isso depois custa uma viagem.
 */
export interface LancamentoConcluido {
  readonly titulo: string;
  readonly texto: string;
  readonly saldo: string | null;
  readonly vaiParaConferencia: boolean;
}

export function resumoDoConcluido(
  rascunho: RascunhoDoLancamento,
  contexto: ContextoDoLancamento,
): LancamentoConcluido {
  const litros = numeroDigitado(rascunho.litros) ?? 0;
  const medidor = numeroDigitado(rascunho.medidor);
  const medida = unidadeDoMedidor(contexto.equipamento);
  const consumo = consumoDoRascunho(rascunho, contexto);
  const unidadeDoConsumo = medida === 'km' ? 'L/km' : 'L/h';
  const partes = [
    `${ZERO(litros)} L de ${contexto.produto?.nome ?? 'combustível'} na ${contexto.equipamento?.codigo ?? 'máquina'}`,
    medidor === null ? null : `medidor ${ZERO(medidor)} ${medida}`,
    consumo === null ? null : `${UM(consumo)} ${unidadeDoConsumo}`,
  ].filter((p): p is string => p !== null);
  const sobra = contexto.saldoDaOrigem === null ? null : contexto.saldoDaOrigem - litros;
  return {
    titulo: 'Abastecimento lançado',
    texto: partes.join(' · '),
    saldo: sobra === null ? null : `Saldo em ${contexto.nomeDaOrigem}: ${ZERO(sobra)} L`,
    vaiParaConferencia:
      consumo !== null &&
      contexto.produto?.categoria === 'combustivel' &&
      foraDaFaixa(consumo, faixaDaMaquina(contexto.equipamento)),
  };
}
